import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useQuotePopup } from "../context/QuotePopupContext";

const PRODUCT_IMG_1 =
  "https://images.unsplash.com/photo-1701166627787-12d9fdd437cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwyfHxncmVlbiUyMGNhcmRhbW9tJTIwbWFjcm98ZW58MHx8fHwxNzgxMjYxMjMwfDA&ixlib=rb-4.1.0&q=85";
const PRODUCT_IMG_2 =
  "https://images.unsplash.com/photo-1758657996330-095d08451cd9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHw0fHxncmVlbiUyMGNhcmRhbW9tJTIwbWFjcm98ZW58MHx8fHwxNzgxMjYxMjMwfDA&ixlib=rb-4.1.0&q=85";

const GRADES = [
  {
    grade: "6mm+",
    title: "Standard",
    desc: "Everyday wholesale grade. Bold aroma, uniform colour, optimised pricing for high-rotation outlets.",
    bestFor: "Retail · Spice traders",
    img: PRODUCT_IMG_1,
  },
  {
    grade: "7mm+",
    title: "Premium",
    desc: "Larger pods, deeper hue, fuller essential oil profile. Our most-shipped grade for hospitality.",
    bestFor: "Hotels · Caterers",
    img: PRODUCT_IMG_2,
  },
  {
    grade: "8mm+",
    title: "Super Premium",
    desc: "Hand-picked bold pods reserved for connoisseur brands. Rich camphor-sweet character.",
    bestFor: "Private label · Sweet shops",
    img: PRODUCT_IMG_1,
  },
  {
    grade: "Jumbo",
    title: "Connoisseur",
    desc: "Estate-rare bold-bold grade. Limited harvest, exceptional presentation, premium positioning.",
    bestFor: "Luxury gifting · Export",
    img: PRODUCT_IMG_2,
  },
];

export const ProductShowcase = () => {
  const { openPopup } = useQuotePopup();
  return (
    <section
      id="products"
      data-testid="products-section"
      className="section-pad bg-[#0A3A2D] text-white relative overflow-hidden"
    >
      {/* Decorative texture */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle at 20% 20%, #C9A227 0, transparent 40%), radial-gradient(circle at 80% 80%, #C9A227 0, transparent 40%)",
        }}
      />
      <div className="container-luxury relative">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-16">
          <div className="md:col-span-7">
            <div className="heading-eyebrow">
              <span className="line" />
              <span className="text">The Product Catalogue</span>
            </div>
            <h2 className="font-serif text-3xl md:text-5xl lg:text-6xl leading-tight">
              Four grades.
              <br />
              <span className="italic font-light text-[#C9A227]">
                One uncompromising standard.
              </span>
            </h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-white/75 text-base md:text-lg leading-relaxed font-light">
              Every pod is calibrated by hand. Larger sizes carry more oil,
              deeper aroma and longer shelf integrity — letting you choose
              precisely the grade your buyer demands.
            </p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {GRADES.map((g, i) => (
            <motion.article
              key={g.grade}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ delay: i * 0.1, duration: 0.7 }}
              className="glass-dark group relative overflow-hidden"
              data-testid={`product-card-${g.grade.replace(/\W/g, "")}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={g.img}
                  alt={`${g.grade} cardamom`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-[1400ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A3A2D] via-[#0A3A2D]/30 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="chip !text-[#C9A227] !border-[#C9A227]/70 bg-[#0A3A2D]/60">
                    {g.title}
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5">
                  <div className="font-serif text-[#C9A227] text-4xl md:text-5xl leading-none">
                    {g.grade}
                  </div>
                  <div className="label-overline text-white/70 mt-1">
                    Bold Green Pods
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-white/75 text-sm leading-relaxed font-light min-h-[80px]">
                  {g.desc}
                </p>
                <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between">
                  <div>
                    <div className="label-overline text-white/40">Best for</div>
                    <div className="text-white/90 text-sm mt-1">{g.bestFor}</div>
                  </div>
                  <button
                    onClick={openPopup}
                    data-testid={`product-cta-${g.grade.replace(/\W/g, "")}`}
                    aria-label={`Enquire about ${g.grade} cardamom`}
                    className="w-10 h-10 flex items-center justify-center border border-[#C9A227]/50 text-[#C9A227] hover:bg-[#C9A227] hover:text-[#0A3A2D] transition-all duration-300"
                  >
                    <ArrowUpRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={openPopup}
            data-testid="products-cta-pricelist"
            className="btn-gold"
          >
            Request Wholesale Price List
          </button>
          <p className="text-white/60 text-sm italic">
            * MOQ from 25 kg · Custom grading available
          </p>
        </div>
      </div>
    </section>
  );
};
